import { useCallback, useState } from "react";
import { hasIndividualInvestorFields, hasCompanyInvestorFields } from "@/domain/investorWizard";
import { UseCaseError } from "@/application/errors/UseCaseError";
import type { UseFormReturn } from "react-hook-form";
import type { Investor } from "@/domain";
import { useCreateIndividualInvestor, useCreateCompanyInvestor } from "@/application/queries";
import {
  toCreateCompanyInvestorRequest,
  toCreateIndividualInvestorRequest,
} from "@/application/mappers/investors/investorWizardPayloads";
import { useOrganizationStore } from "@/stores/useOrganizationStore";
import type {
  InvestorWizardFormData,
  InvestorWizardFormValues,
} from "@/domain/validation/investorSchemas";

interface UseAddInvestorSubmitParams {
  form: UseFormReturn<InvestorWizardFormData, unknown, InvestorWizardFormValues>;
  triggerCurrentStep: () => Promise<boolean>;
  onInvestorAdded?: (investor: Investor) => void;
  handleClose: () => void;
}

export function useAddInvestorSubmit({
  form,
  triggerCurrentStep,
  onInvestorAdded,
  handleClose,
}: UseAddInvestorSubmitParams) {
  const orgId = useOrganizationStore((state) => state.selectedOrganizationUuid ?? "");
  const createIndividual = useCreateIndividualInvestor(orgId);
  const createCompany = useCreateCompanyInvestor(orgId);
  const [error, setError] = useState<unknown>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const clearError = useCallback(() => setError(null), []);

  const handleSubmit = useCallback(async () => {
    const isValid = await triggerCurrentStep();
    if (!isValid) return;

    setError(null);
    setIsSubmitting(true);
    try {
      const data = form.getValues();
      let result: Investor;

      if (hasIndividualInvestorFields(data)) {
        result = await createIndividual.mutateAsync(toCreateIndividualInvestorRequest(data));
      } else if (hasCompanyInvestorFields(data)) {
        result = await createCompany.mutateAsync(toCreateCompanyInvestorRequest(data));
      } else {
        throw new UseCaseError("Invalid investor identity", "REQUEST_VALIDATION_FAILED");
      }

      onInvestorAdded?.(result);
      handleClose();
    } catch (cause) {
      setError(cause);
    } finally {
      setIsSubmitting(false);
    }
  }, [triggerCurrentStep, form, createIndividual, createCompany, onInvestorAdded, handleClose]);

  return { isSubmitting, error, clearError, handleSubmit };
}
